import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cad-arc';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { useAssociateBolo } from './use-associate-bolo';
import {
    AssociateEntityForm,
    AssociateEntityFormDisplayType,
} from '../../associate-event/components/associate-entity-form';

export type AssociateBoloModalComponentType = typeof AssociateBoloModal;

export interface AssociateBoloModalProps {
    boloId: number;
    entityType: 'BOLO' | 'EVENT';

    portalProps?: ModalProps['portalProps'];
    onSuccess?: (...args: unknown[]) => unknown;
}

export const AssociateBoloModal = ({
    boloId,
    portalProps,
    onSuccess,
    entityType,
}: AssociateBoloModalProps) => {
    const modalControls = useFullscreenModalControls();

    const onCancel = React.useCallback(
        function onCancel(): void {
            modalControls.close();
        },
        [modalControls]
    );

    const handleSuccess = useCallback(() => {
        onSuccess?.();
        modalControls.close();
    }, [onSuccess, modalControls]);

    const mutation = useAssociateBolo(boloId, handleSuccess, entityType);

    const form = useForm<AssociateEntityFormDisplayType>({
        mode: 'onBlur',
        defaultValues: {
            entityId: boloId,
            associatedEntities: [],
            reason: '',
        },
    });

    const associatedEntities = form.watch('associatedEntities');

    return (
        <IntegratedFormProvider mutation={mutation} form={form}>
            <BlankModal
                portalProps={portalProps}
                size="md"
                onClose={onCancel}
                isOpen={true}
                className="associate-bolo-modal"
            >
                <ModalHeader>Associate BOLO</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <FormProvider {...form}>
                            <AssociateEntityForm entityType={entityType} />
                        </FormProvider>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton disabled={!associatedEntities.length}>
                            Associate BOLO
                        </FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
